import {
  Audience,
  AudienceTravelTime,
  BuildAudience,
  GeoFenceLocation,
  SiteAudience,
  SiteCatchment,
  SiteConfig,
  UploadAudience,
  Variable,
} from "@/types";

import {
  createBuildDefinition,
  createSiteDefinition,
  createUploadDefinition,
  defaultTravelTime,
  defaultVariableGroup
} from "@/utils";

// All helpers return a new audience, or the same one when nothing changes.
// They never modify the audience they're given.

export const createDefinition = (audience: Audience | null) => {
  if (!audience) return '';

  if (audience.audience_type === 'build') {
    return createBuildDefinition(audience as BuildAudience);
  } else if (audience.audience_type === 'upload') {
    return createUploadDefinition(audience as UploadAudience)
  } else if (audience.audience_type === 'site') {
    return createSiteDefinition(audience as SiteAudience)
  } else {
    return '' // TODO
  }
}

export const addQueryGroup = (audience: BuildAudience): BuildAudience => {
  const ids = audience.var_groups.map((v) => v.id);
  const newId = ids.length > 0 ? Math.max(...ids) + 1 : 1;
  return {
    ...audience,
    var_groups: [...audience.var_groups, defaultVariableGroup(newId)],
  };
};

export const removeQueryGroup = (
  audience: BuildAudience,
  id: number,
): BuildAudience => {
  if (!audience.var_groups.some((v) => v.id === id)) return audience;

  return {
    ...audience,
    var_groups: audience.var_groups
      .filter((v) => v.id !== id)
      .map((v, idx) => ({ ...v, id: idx + 1 })),
  };
};

export const resetQueryGroup = (
  audience: BuildAudience,
  id: number,
): BuildAudience => {
  if (!audience.var_groups.some((v) => v.id === id)) return audience;

  return {
    ...audience,
    var_groups: audience.var_groups.map((v) => (v.id === id ? defaultVariableGroup(id) : v)),
  };
};

export const changeOperator = (
  audience: BuildAudience,
  operator: string,
  id?: number | null,
): BuildAudience => {
  if (id !== null && id !== undefined) {
    const targetGroup = audience.var_groups.find(g => g.id === id);
    if (!targetGroup || targetGroup.operator === operator) {
      return audience;
    }
    return {
      ...audience,
      var_groups: audience.var_groups.map((group) =>
        group.id === id
          ? { ...group, operator }
          : group
      ),
    };
  } else {
    if (audience.operator === operator) {
      return audience;
    }
    return {
      ...audience,
      operator,
    };
  }
};

export const changeVariables = (
  audience: BuildAudience,
  id: number,
  variables: Variable[],
): BuildAudience => {
  if (!audience.var_groups.some((v) => v.id === id)) return audience;

  return {
    ...audience,
    var_groups: audience.var_groups.map((group) =>
      group.id === id ? { ...group, variables } : group
    ),
  };
};

// Adds variables to a group, skipping any it already has
export const addVariables = (
  audience: BuildAudience,
  id: number,
  variables: Variable[],
): BuildAudience => {
  const group = audience.var_groups.find((v) => v.id === id);
  if (!group) return audience;

  const existing = new Set(group.variables.map((v) => v.tag_id));
  const added = variables.filter((v) => !existing.has(v.tag_id));
  return added.length === 0 ? audience : changeVariables(audience, id, [...group.variables, ...added]);
};

export const removeVariable = (
  audience: BuildAudience,
  id: number,
  tagId: number,
): BuildAudience => {
  const group = audience.var_groups.find((v) => v.id === id);
  if (!group || !group.variables.some((v) => v.tag_id === tagId)) return audience;

  return changeVariables(audience, id, group.variables.filter((v) => v.tag_id !== tagId));
};

export const changeBuildMode = (
  audience: BuildAudience,
  build_mode: string,
): BuildAudience => {
  if (audience.build_mode === build_mode) return audience;
  return {
    ...audience,
    build_mode,
  };
};

export const addUploadFile = (audience: UploadAudience, upload_file: File | null): UploadAudience => {
  return {
    ...audience,
    upload_file,
    audience_size: undefined,
  };
};

export const changeUploadMode = (
  audience: UploadAudience,
  upload_mode: string,
): UploadAudience => {
  if (audience.upload_mode === upload_mode) return audience;
  return {
    ...audience,
    upload_mode,
    audience_size: undefined,
    travel_time: upload_mode === 'travel_time' ? { ...defaultTravelTime } : undefined,
  };
};

export const setGeoFence = (
  audience: BuildAudience,
  geo_fence: GeoFenceLocation[],
): BuildAudience => {
  return {
    ...audience,
    geo_fence,
  };
};

export const changeName = <T extends Audience>(
  audience: T,
  audienceName: string,
  projectName: string | null,
): T => {
  return {
    ...audience,
    audience_name: audienceName.trim(),
    project_name: projectName ? projectName.trim() : projectName,
  };
};

// Merges the changes into the audience's travel time, filling gaps from the defaults
export const changeTravelTime = (
  audience: UploadAudience,
  changes: Partial<AudienceTravelTime>
): UploadAudience => {
  const traveltime = { ...audience.travel_time, ...changes };
  return {
    ...audience,
    travel_time: {
      mode: traveltime.mode ?? defaultTravelTime.mode,
      transportation: traveltime.transportation ?? defaultTravelTime.transportation,
      duration: traveltime.duration ?? defaultTravelTime.duration,
      departure_day: traveltime.departure_day ?? defaultTravelTime.departure_day,
      departure_time: traveltime.departure_time ?? defaultTravelTime.departure_time
    },
    audience_size: undefined,
  };
};

const changeSiteConfig = (
  audience: SiteAudience,
  changes: Partial<SiteConfig>,
): SiteAudience => {
  return {
    ...audience,
    site_config: { ...audience.site_config, ...changes },
  };
};

export const setSites = (audience: SiteAudience, sites: number[]) =>
  changeSiteConfig(audience, { sites });

export const setSiteCatchment = (audience: SiteAudience, catchment: SiteCatchment[]) =>
  changeSiteConfig(audience, { catchment });

export const setSiteCategories = (audience: SiteAudience, categories: string[]) =>
  changeSiteConfig(audience, { categories });

export const setSiteDayType = (audience: SiteAudience, day_type: string) =>
  changeSiteConfig(audience, { day_type });
